import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { UmmeldungService } from '../../service/ummeldung/ummeldung.service';
import { computed, inject } from '@angular/core';
import { UmmeldungRequest } from '../../../generated/model/ummeldung-request.model';
import { map, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { Ummeldung } from '../../../generated/model/ummeldung.model';
import { Kunde } from '../../../generated/model/kunde.model';
import { withSessionStorage, withStorageSync } from '@angular-architects/ngrx-toolkit';
import { UmmeldungMetadata } from '../../../generated/model/ummeldung-metadata.model';
import { withTreeShakableDevTools } from '../../store/features/devTools.feature';
import { MeterPoint } from '../../model/meter-point.model';
import { Company } from '@shared-ikp/model/company.model';
import { isDatumStepComplete, isProduktStepComplete, resolveBisherigerVertragspartnerMode } from './utils';
import { UnterschreibendePartei } from './models';
import { withRequestTracking } from '../../store/features/request-tracking.feature';
import { MeterpointStatus } from '../../model/meterpoint-status.model';
import { findCompanyByGpNr } from '../../service/company/company.utils';
import { UmmeldungStatus } from '../../../generated/model/ummeldung-status.model';
import { withUmmeldungLocking } from './ummeldung.lock.store';

export interface UmmeldungState {
  activeUmmeldung: Ummeldung | null;
  aktuellerZaehlpunkt: MeterPoint | null;
  zaehlpunktUnternehmen: Company | null;
  accountUnternehmen: Company[];
  verwalteteUnternehmen: Company[];
  metadata: UmmeldungMetadata | null;
  loading: boolean;
  error: HttpErrorResponse | null;
  uploadMode: boolean;
}

const initialState: UmmeldungState = {
  activeUmmeldung: null,
  aktuellerZaehlpunkt: null,
  zaehlpunktUnternehmen: null,
  accountUnternehmen: [],
  verwalteteUnternehmen: [],
  metadata: null,
  loading: false,
  error: null,
  uploadMode: false,
};

export const UmmeldungStore = signalStore(
  withTreeShakableDevTools('UmmeldungStore'),
  withState<UmmeldungState>(initialState),
  withStorageSync(
    {
      key: 'ummeldung',
      select: (state: UmmeldungState) => ({
        activeUmmeldung: state.activeUmmeldung,
        aktuellerZaehlpunkt: state.aktuellerZaehlpunkt,
        uploadMode: state.uploadMode,
      }),
    },
    withSessionStorage(),
  ),
  withRequestTracking(),
  withUmmeldungLocking(),
  withComputed(store => ({
    isZaehlpunktAktiv: computed(() => store.aktuellerZaehlpunkt()?.status === MeterpointStatus.AKTIV),
    bisherigerVertragspartnerMode: computed(() => resolveBisherigerVertragspartnerMode(store.aktuellerZaehlpunkt())),
    rechnungsempfaengerUnternehmen: computed(() =>
      findCompanyByGpNr(store.accountUnternehmen(), store.aktuellerZaehlpunkt()?.rechnungsempfaengerGpNr),
    ),
    isProduktAndDatumStepFrozen: computed(() => {
      const ummeldung = store.activeUmmeldung();
      return !!ummeldung?.bisherigerKunde?.unterschrift || !!ummeldung?.neuerKunde?.unterschrift;
    }),
    isProduktAndDatumStepComplete: computed(() => {
      const ummeldung = store.activeUmmeldung();
      return isProduktStepComplete(ummeldung) && isDatumStepComplete(ummeldung);
    }),
    isUmmeldungInCompletedStatus: computed(
      () =>
        store.activeUmmeldung()?.status === UmmeldungStatus.UEBERMITTELT ||
        store.activeUmmeldung()?.status === UmmeldungStatus.ABGESCHLOSSEN,
    ),
  })),
  withMethods((store, service = inject(UmmeldungService)) => ({
    updateActiveUmmeldung(partial: Partial<Ummeldung>): void {
      const activeUmmeldung = store.activeUmmeldung();
      if (activeUmmeldung) {
        patchState(store, { activeUmmeldung: { ...activeUmmeldung, ...partial } });
      }
    },
    revokeUnterschriften(parteien: UnterschreibendePartei[]): void {
      const activeUmmeldung = store.activeUmmeldung();
      const kundeFor = (partei: UnterschreibendePartei): Kunde | undefined => {
        const kunde = activeUmmeldung?.[partei];
        return kunde && parteien.includes(partei) ? { ...kunde, unterschrift: undefined } : kunde;
      };
      this.updateActiveUmmeldung({ bisherigerKunde: kundeFor('bisherigerKunde'), neuerKunde: kundeFor('neuerKunde') });
    },
    createUmmeldung(ummeldungRequest: UmmeldungRequest): Observable<void> {
      return store.trackRequest(service.createUmmeldung({ ummeldungRequest })).pipe(
        tap((id: string) => patchState(store, { activeUmmeldung: { id, ...ummeldungRequest.ummeldung } })),
        map(() => void 0),
      );
    },
    updateUmmeldung(id: string, ummeldungRequest: UmmeldungRequest): Observable<void> {
      return store.trackRequest(service.updateUmmeldung({ id, ummeldungRequest })).pipe(map(() => void 0));
    },
    deleteUmmeldung(id: string): Observable<void> {
      return store.trackRequest(service.deleteUmmeldung({ id })).pipe(map(() => void 0));
    },
    submitUmmeldung(id: string): Observable<void> {
      return store.trackRequest(service.submitUmmeldung({ id })).pipe(
        tap(() => this.updateActiveUmmeldung({ status: 'UEBERMITTELT' })),
        map(() => void 0),
      );
    },
    uploadUmmeldungPdf(id: string, file: File, filename: string): Observable<void> {
      return store.trackRequest(service.uploadUmmeldungPdf({ id, file: file }, filename)).pipe(map(() => void 0));
    },
    getUmmeldungPdf(id: string): Observable<Blob> {
      return service.getUmmeldungPdf({ id });
    },
    saveActiveUmmeldung(aktuellerProzessSchritt: Ummeldung.AktuellerProzessSchrittEnum | null): Observable<void> {
      const activeUmmeldung = store.activeUmmeldung();
      if (!activeUmmeldung) {
        return of(void 0);
      }
      const ummeldung: Ummeldung = { ...activeUmmeldung, status: UmmeldungStatus.BEGONNEN, aktuellerProzessSchritt };
      if (activeUmmeldung.id) {
        return this.updateUmmeldung(activeUmmeldung.id, { ummeldung });
      }
      return this.createUmmeldung({ ummeldung });
    },
    clearError(): void {
      patchState(store, { error: null });
    },
    clearStore(): void {
      patchState(store, initialState, { lockExpiresAt: null });
      sessionStorage.removeItem('ummeldung');
    },
  })),
);
